/**
 * How much of a company's shift-type colour the card's text may carry (review V04). The card is the
 * colour at 11 % over the card surface and its text the colour mixed into the ink; any colour can be
 * chosen in Innstillinger, so the share is the largest that still reads at 4.5:1 in both themes.
 * Token values from tokens.css: light card #ffffff ink #101c2f, dark card #1f2633 ink #eef1f6.
 */
type Rgb = [number, number, number];
const THEMES: { card: Rgb; ink: Rgb }[] = [
  { card: [255, 255, 255], ink: [16, 28, 47] },
  { card: [31, 38, 51], ink: [238, 241, 246] },
];
const SHARES = [65, 50, 35, 20, 0];
const hex = (h: string): Rgb | null => {
  const m = /^#?([0-9a-f]{6}|[0-9a-f]{3})$/i.exec(h.trim());
  if (!m) return null;
  const x =
    (m[1] as string).length === 3 ? [...(m[1] as string)].map((c) => c + c).join('') : (m[1] as string);
  return [0, 2, 4].map((i) => Number.parseInt(x.slice(i, i + 2), 16)) as Rgb;
};
const mix = (a: Rgb, b: Rgb, pa: number): Rgb => a.map((v, i) => v * pa + (b[i] as number) * (1 - pa)) as Rgb;
const lum = (c: Rgb) => {
  const [r, g, b] = c.map((v) => {
    const s = v / 255;
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  }) as Rgb;
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
};
export const contrast = (a: Rgb, b: Rgb) => {
  const [x, y] = [lum(a), lum(b)].sort((p, q) => q - p) as [number, number];
  return (x + 0.05) / (y + 0.05);
};
/** The share in percent; 0 is plain ink. A colour we cannot read (a token) gets the safe 35. */
export function inkShare(color: string): number {
  const c = hex(color);
  if (!c) return 35;
  return (
    SHARES.find((p) => THEMES.every((t) => contrast(mix(c, t.ink, p / 100), mix(c, t.card, 0.11)) >= 4.5)) ??
    0
  );
}
export const _test = { hex, mix };
